import React, { useMemo } from 'react';
import { Table, Typography, Checkbox, Tag } from 'antd';
import { useTender } from '../../../contexts/TenderContext';
import { useGlobalSettings } from '../../../hooks/useGlobalSettings';
import { useItemLibrary } from '../../../hooks/useItemLibrary';
import { useRoadsAndSewersTotalSync } from '../../../hooks/useRoadsAndSewersTotalSync';
import { COLORS } from '../../../utils/constants';

const { Title, Text } = Typography;

interface RsSumAnalysisBillSummaryProps {
  recalculateTrigger?: number;
  includeDiscountDisplay?: boolean;
  onDiscountCheckboxChange?: (checked: boolean) => void;
  includePreliminariesDisplay?: boolean;
  onPreliminariesCheckboxChange?: (checked: boolean) => void;
  includeOhpDisplay?: boolean;
  onOhpCheckboxChange?: (checked: boolean) => void;
  boqStatus?: string;
  isHistoricVersion?: boolean;
}

interface SumRow {
  key: string;
  description: string;
  amount: string;
  isTenderTotal: boolean;
  isDiscount: boolean;
}

const RsSumAnalysisBillSummary: React.FC<RsSumAnalysisBillSummaryProps> = ({
  recalculateTrigger = 0,
  includeDiscountDisplay = false,
  onDiscountCheckboxChange,
  includePreliminariesDisplay = false,
  onPreliminariesCheckboxChange,
  includeOhpDisplay = false,
  onOhpCheckboxChange,
  boqStatus,
  isHistoricVersion = false,
}) => {
  const { state } = useTender();
  const { settings: globalSettings } = useGlobalSettings();
  const { data: itemLibraryData, isLoading: isItemLibraryLoading } = useItemLibrary(state.region || '');

  const currentBoqStatus = boqStatus || state.boqStatus || 'In-progress';

  const {
    foulTotal, stormTotal, roadsTotal, mainServicesTotal, substationTotal, posTotal, compoundTotal, prelimsTotal,
    pumpStationTotal, risingMainTotal,
    pondsSwalesTotal, cellularAttenuationTotal, weholiteAttenuationTotal, concAttenuationTotal, boxCulvertsTotal,
    dynamicBillSummaryTabTotals,
    prelimsExtraItemsTotal, foulSewersExtraItemsTotal, stormSewersExtraItemsTotal, roadsExtraItemsTotal,
    mainServicesExtraItemsTotal, substationExtraItemsTotal, posExtraItemsTotal, compoundExtraItemsTotal,
    pumpStationExtraItemsTotal, risingMainExtraItemsTotal,
    pondsSwalesExtraItemsTotal, cellularAttenuationExtraItemsTotal, weholiteAttenuationExtraItemsTotal,
    concAttenuationExtraItemsTotal, boxCulvertsExtraItemsTotal,
    discountAmount, finalTotal,
  } = useRoadsAndSewersTotalSync(itemLibraryData, isItemLibraryLoading, {
      includeOhpDisplay,
      includePreliminariesDisplay,
      includeDiscountDisplay,
      recalculateTrigger,
    });

  const fmtCurrency = (v: number) =>
    `£${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const weholiteSupplierName = state.weholiteAttenuation?.config?.supplierName ?? 'Weholite';

  // Each domain's ad-hoc "Add Section"/"Add Item" extra rows
  // (useBillSummaryExtraItems.tsx) fold straight into that domain's own row
  // below — never a separate "(Extra Items)" row — same convention
  // Groundworks' own SumAnalysis.tsx follows for cutFillsExtraItems etc.
  // (extras are part of what that Bill Summary tab's own footer total already
  // shows, so Sum Analysis must include them to tie out with it).
  const sections = useMemo(
    () => [
      { key: 'prelims', name: 'Preliminary Items', amount: prelimsTotal + prelimsExtraItemsTotal },
      { key: 'foul', name: 'Foul Sewers', amount: foulTotal + foulSewersExtraItemsTotal },
      { key: 'pump-station', name: 'Pump Station', amount: pumpStationTotal + pumpStationExtraItemsTotal },
      { key: 'rising-main', name: 'Rising Main', amount: risingMainTotal + risingMainExtraItemsTotal },
      { key: 'storm', name: 'Storm Sewers', amount: stormTotal + stormSewersExtraItemsTotal },
      { key: 'ponds-swales', name: 'Ponds & Swales', amount: pondsSwalesTotal + pondsSwalesExtraItemsTotal },
      { key: 'cellular', name: 'Cellular Attenuation', amount: cellularAttenuationTotal + cellularAttenuationExtraItemsTotal },
      { key: 'weholite', name: `${weholiteSupplierName} Attenuation`, amount: weholiteAttenuationTotal + weholiteAttenuationExtraItemsTotal },
      { key: 'conc', name: 'Conc Attenuation', amount: concAttenuationTotal + concAttenuationExtraItemsTotal },
      { key: 'box-culverts', name: 'Box Culverts', amount: boxCulvertsTotal + boxCulvertsExtraItemsTotal },
      { key: 'roads', name: 'Roads', amount: roadsTotal + roadsExtraItemsTotal },
      { key: 'main-services', name: 'Main Services', amount: mainServicesTotal + mainServicesExtraItemsTotal },
      { key: 'substation', name: 'Substation', amount: substationTotal + substationExtraItemsTotal },
      { key: 'pos', name: 'POS', amount: posTotal + posExtraItemsTotal },
      { key: 'compound', name: 'Compound', amount: compoundTotal + compoundExtraItemsTotal },
    ],
    [
      prelimsTotal, foulTotal, pumpStationTotal, risingMainTotal, stormTotal,
      pondsSwalesTotal, cellularAttenuationTotal, weholiteAttenuationTotal, concAttenuationTotal, boxCulvertsTotal,
      roadsTotal, mainServicesTotal, substationTotal, posTotal, compoundTotal, weholiteSupplierName,
      prelimsExtraItemsTotal, foulSewersExtraItemsTotal, stormSewersExtraItemsTotal, roadsExtraItemsTotal,
      mainServicesExtraItemsTotal, substationExtraItemsTotal, posExtraItemsTotal, compoundExtraItemsTotal,
      pumpStationExtraItemsTotal, risingMainExtraItemsTotal,
      pondsSwalesExtraItemsTotal, cellularAttenuationExtraItemsTotal, weholiteAttenuationExtraItemsTotal,
      concAttenuationExtraItemsTotal, boxCulvertsExtraItemsTotal,
    ],
  );

  const processedData = useMemo((): SumRow[] => {
    const items: SumRow[] = [];

    sections.forEach((section) => {
      if (section.amount === 0) return;
      items.push({
        key: section.key,
        description: section.name,
        amount: fmtCurrency(section.amount),
        isTenderTotal: false,
        isDiscount: false,
      });
    });

    // Dynamic Bill Summary tabs (user-added "+ Add Tab" sections) each show up as
    // their own Sum Analysis row and roll into the grand total, same as every
    // fixed domain section above — always shown, even at £0.00, since a tab is a
    // real thing the user explicitly created (same convention Groundworks' own
    // SumAnalysis.tsx follows for its dynamicBillSummaryTabs).
    dynamicBillSummaryTabTotals.forEach((tab) => {
      items.push({
        key: `dynamic-${tab.id}`,
        description: tab.label,
        amount: fmtCurrency(tab.amount),
        isTenderTotal: false,
        isDiscount: false,
      });
    });

    if (discountAmount > 0) {
      items.push({
        key: 'discount',
        description: `DISCOUNT (${globalSettings.roadsAndSewers.discount}%)`,
        amount: `-${fmtCurrency(discountAmount)}`,
        isTenderTotal: false,
        isDiscount: true,
      });
    }

    items.push({
      key: 'tender-total',
      description: 'TENDER TOTAL',
      amount: fmtCurrency(finalTotal),
      isTenderTotal: true,
      isDiscount: false,
    });

    return items;
  }, [sections, dynamicBillSummaryTabTotals, discountAmount, finalTotal, globalSettings.roadsAndSewers.discount]);

  const tagColor = isHistoricVersion
    ? 'default'
    : currentBoqStatus.toLowerCase() === 'in-progress'
    ? 'blue'
    : currentBoqStatus === 'Under Review - Senior Estimator'
    ? 'orange'
    : currentBoqStatus === 'Under Review Commercial Director'
    ? 'gold'
    : currentBoqStatus === 'Under Client Review'
    ? 'green'
    : currentBoqStatus.toLowerCase() === 'won'
    ? 'purple'
    : 'default';

  return (
    <div style={{ padding: '16px' }}>
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Title
            level={5}
            style={{ margin: 0, fontWeight: 600, fontSize: '16px', color: '#111827' }}
          >
            Sum Analysis Bill Summary
          </Title>
          {currentBoqStatus && (
            <Tag color={tagColor} style={{ fontSize: '13px', fontWeight: 600 }}>
              {currentBoqStatus}
            </Tag>
          )}
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {onOhpCheckboxChange && (
            <Checkbox
              checked={includeOhpDisplay && globalSettings.roadsAndSewers.overheadAndProfit > 0}
              onChange={(e) => onOhpCheckboxChange(e.target.checked)}
              disabled
            >
              OH&P ({globalSettings.roadsAndSewers.overheadAndProfit || 0}%)
            </Checkbox>
          )}
          {onPreliminariesCheckboxChange && (
            <Checkbox
              checked={includePreliminariesDisplay && globalSettings.roadsAndSewers.preliminaries > 0}
              onChange={(e) => onPreliminariesCheckboxChange(e.target.checked)}
              disabled
            >
              Preliminaries ({globalSettings.roadsAndSewers.preliminaries || 0}%)
            </Checkbox>
          )}
          {onDiscountCheckboxChange && (
            <Checkbox
              checked={includeDiscountDisplay && globalSettings.roadsAndSewers.discount > 0}
              onChange={(e) => onDiscountCheckboxChange(e.target.checked)}
              disabled={state.isViewMode || !globalSettings.roadsAndSewers.discount || globalSettings.roadsAndSewers.discount === 0}
            >
              Apply Discount ({globalSettings.roadsAndSewers.discount || 0}%)
            </Checkbox>
          )}
        </div>
      </div>

      <style>{`
        .rs-sum-analysis-table .ant-table { border-radius: 0 !important; }
        .rs-sum-analysis-table .ant-table-container { border-radius: 0 !important; }
        .ant-checkbox-wrapper.ant-checkbox-wrapper-disabled span { color: rgba(0,0,0,0.65) !important; }
      `}</style>

      <Table<SumRow>
        className="rs-sum-analysis-table"
        dataSource={processedData}
        rowKey="key"
        pagination={false}
        size="middle"
        bordered
        rowClassName={(record) => (record.isTenderTotal || record.isDiscount ? 'bg-blue-50' : '')}
        columns={[
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (value: string, record: SumRow) => {
              if (record.isTenderTotal) {
                return <Text strong style={{ fontSize: '14px', fontWeight: 700 }}>{value}</Text>;
              }
              return <Text style={{ fontSize: '14px' }}>{value}</Text>;
            },
          },
          {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 200,
            align: 'right' as const,
            render: (value: string, record: SumRow) => {
              if (!value) return '';
              if (record.isTenderTotal) {
                return <Text strong style={{ fontSize: '14px', fontWeight: 700 }}>{value}</Text>;
              }
              return <Text strong style={{ fontSize: '14px', fontWeight: 800 }}>{value}</Text>;
            },
          },
        ]}
      />

      <style>{`
        .rs-sum-analysis-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }
      `}</style>
    </div>
  );
};

export default RsSumAnalysisBillSummary;
